import { Component, Input, OnInit } from '@angular/core';
import { ErrorHandlerService } from '@cashbox-app/service/error-handler.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule, ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { HxAuthService, HxInvoiceService, HxStoreService, HxUiLabelPipe, ProductInfoType, StoreBasicModel, StoreProductModel } from 'hx-services';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ToastrService } from 'ngx-toastr';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import { NgFor, NgIf } from '@angular/common';
import { StoreProductsSearchComponent } from '../../components/store-products-search/store-products-search.component';

export type InvoiceMode = 'EXPORT' | 'RESTORATION';

@Component({
  selector: 'app-balance-new.m-grid__item.m-grid__item--fluid',
  templateUrl: './balance-new.modal.html',
  styleUrls: ['./balance-new.modal.css'],
  imports: [FormsModule, ReactiveFormsModule, NgSelectComponent, NgFor, NgOptionComponent, StoreProductsSearchComponent, NgIf, TranslocoPipe, HxUiLabelPipe]
})
export class BalanceNewModal implements OnInit {
  @Input() storeId!: number;
  invoiceFormKeys = {
    fromStoreId: 'fromStoreId',
    toStoreId: 'toStoreId',
    description: 'description',
    products: 'products'
  };
  productFormKeys = {
    productInfoId: 'productInfoId',
    amount: 'amount'
  };
  isLoading = {
    submit: false,
  };
  invoiceForm!: UntypedFormGroup;
  stores: StoreBasicModel[] = [];
  productInfoTypes: ProductInfoType[] = [ProductInfoType.PROPERTY, ProductInfoType.PRODUCT, ProductInfoType.MATERIAL_ASSETS, ProductInfoType.NON_MATERIAL_ASSETS];
  mode: InvoiceMode = 'EXPORT';
  private allStores: StoreBasicModel[] = [];

  constructor(
    private toastr: ToastrService,
    private errorService: ErrorHandlerService,
    private modalInstance: NgbActiveModal,
    private fb: UntypedFormBuilder,
    private invoiceService: HxInvoiceService,
    private storeService: HxStoreService,
    private auth: HxAuthService,
    private tr: TranslocoService
  ) {
  }

  ngOnInit(): void {
    this.createForm();
    this.loadStores();
  }

  dismissModal() {
    this.modalInstance.dismiss();
  }

  onSubmitForm() {
    // eslint-disable-next-line guard-for-in
    for (const i in this.invoiceForm.controls) {
      this.invoiceForm.controls[i].markAsDirty();
      this.invoiceForm.controls[i].updateValueAndValidity();
    }
    if (this.invoiceForm.valid) {
      this.invoiceForm.disable();
      this.isLoading.submit = true;
      const obj = { ...this.invoiceForm.getRawValue(), restoration: this.mode === 'RESTORATION' };
      this.invoiceService.createInvoice(obj).subscribe(() => {
        this.toastr.success(this.tr.translate('balance-new.access'));
        this.isLoading.submit = false;
        this.modalInstance.close();
      }, err => {
        this.isLoading.submit = false;
        this.errorService.handle(err);
      });
    }
  }

  addItem() {
    this.asFormArray(this.invoiceFormKeys.products).push(this.createItem());
  }

  removeItem(index: number) {
    this.asFormArray(this.invoiceFormKeys.products).removeAt(index);
  }

  asFormArray(path: string): UntypedFormArray {
    return this.invoiceForm.get(path) as UntypedFormArray;
  }

  onStoreChanged(store?: number) {
    this.productInfoTypesChange(store);
  }

  onModeChanged(mode: InvoiceMode) {
    this.mode = mode;
    this.invoiceForm.get(this.invoiceFormKeys.toStoreId)?.setValue(null);
    this.applyToStoreFilter();
  }

  onStoreProductSelected(item: StoreProductModel, index: number): void {
    this.asFormArray(this.invoiceFormKeys.products).at(index).patchValue({productInfoId: item.productInfo.id});
  }

  private createForm(): void {
    this.invoiceForm = this.fb.group({
      fromStoreId: [this.storeId, [Validators.required]],
      toStoreId: [null, [Validators.required]],
      description: [null],
      products: this.fb.array([], [Validators.required]),
    });
  }

  private createItem() {
    return this.fb.group({
      productInfoId: [null, [Validators.required]],
      amount: [null, [Validators.required, Validators.min(0)]],
    });
  }

  private loadStores(): void {
    const storeId = this.auth.user.store.id;
    this.storeService.getOwnerAllStores(storeId)
      .subscribe({
        next: stores => {
          this.allStores = stores.filter(r => r.active);
          this.applyToStoreFilter();
        },
        error: err => this.errorService.handle(err)
      });
  }

  private applyToStoreFilter() {
    const currentStoreId = this.auth.user.store.id;
    if (this.mode === 'RESTORATION') {
      this.stores = this.allStores;
    } else {
      this.stores = this.allStores.filter(r => r.id !== currentStoreId);
    }
  }

  private productInfoTypesChange(item?: number) {
    if (item && [21,53,12,355,85,36,313,111,335].includes(item)) {
      this.productInfoTypes.push(ProductInfoType.INGREDIENT);
    } else {
      if (this.productInfoTypes.includes(ProductInfoType.INGREDIENT)) {
        this.productInfoTypes.splice(this.productInfoTypes.indexOf(ProductInfoType.INGREDIENT), 1);
      }
    }
  }
}
